import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    BarChart3,
    Baby,
    ClipboardCheck,
    TrendingUp,
    Award,
    GitCompare
} from 'lucide-react';

// Import existing hospital report
import { HospitalReport } from '@/components/reports/HospitalReport';

// Import new hospital report components (to be created)
import { PrematureStatisticsReport } from '@/components/reports/hospital/PrematureStatisticsReport';
import { VonComplianceReport } from '@/components/reports/hospital/VonComplianceReport';
import { TrendsAnalysisReport } from '@/components/reports/hospital/TrendsAnalysisReport';
import { QualityIndicatorsReport } from '@/components/reports/hospital/QualityIndicatorsReport';
import { ComparativeAnalysisReport } from '@/components/reports/hospital/ComparativeAnalysisReport';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';

export const HospitalReportsPage: React.FC = () => {
    const { utilizator } = useAuth();
    const { activeSection } = useSectionContext();
    const isPrematuriSection = activeSection === 'prematuri';
    const [activeTab, setActiveTab] = useState(isPrematuriSection ? 'premature' : 'hospital');

    const isAdmin = utilizator?.user_level_id === 3 || utilizator?.user_level?.id === 3;

    const allReportCards = [
        {
            id: 'hospital',
            title: 'Statistici generale spital',
            description: 'Distribuție MCC, severitate și tendințe',
            icon: BarChart3,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
        },
        {
            id: 'premature',
            title: 'Statistici pacienți prematuri',
            description: 'Date RONN și performanță TIN',
            icon: Baby,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100',
        },
        {
            id: 'von-compliance',
            title: 'Conformitate registru RONN',
            description: 'Pregătire trimitere și calitate date',
            icon: ClipboardCheck,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
        },
        {
            id: 'trends',
            title: 'Analiza tendințe',
            description: 'Tendințe lunare/anuale de internări',
            icon: TrendingUp,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100',
        },
        {
            id: 'quality',
            title: 'Indicatori de calitate',
            description: 'KPI-uri și benchmarking',
            icon: Award,
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-100',
        },
    ];

    // Add comparative analysis for admins
    if (isAdmin) {
        allReportCards.push({
            id: 'comparative',
            title: 'Analiza comparativă spitale',
            description: 'Comparație multi-spital (Admin)',
            icon: GitCompare,
            color: 'text-red-600',
            bgColor: 'bg-red-100',
        });
    }

    // premature/von-compliance are prematuri-only; hospital (MCC distribution)
    // is MCC-only - don't show either section's reports in the other section
    const reportCards = useMemo(
        () =>
            allReportCards.filter((report) => {
                if (report.id === 'premature' || report.id === 'von-compliance') {
                    return isPrematuriSection;
                }
                if (report.id === 'hospital') {
                    return !isPrematuriSection;
                }
                return true;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isPrematuriSection, isAdmin]
    );

    useEffect(() => {
        if (!reportCards.some((report) => report.id === activeTab)) {
            setActiveTab(reportCards[0]?.id ?? 'hospital');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportCards]);

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Rapoarte spital</h1>
                    <p className="text-muted-foreground mt-2">
                        Rapoarte focusate pe statistici la nivel de spital și perspective administrative
                    </p>
                </div>

                {/* Report Cards Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {reportCards.map((report) => {
                        const Icon = report.icon;
                        return (
                            <Card
                                key={report.id}
                                className={`cursor-pointer hover:shadow-lg transition-all ${activeTab === report.id ? 'ring-2 ring-primary' : ''
                                    }`}
                                onClick={() => setActiveTab(report.id)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {report.title}
                                    </CardTitle>
                                    <div className={`rounded-full p-2 ${report.bgColor}`}>
                                        <Icon className={`h-4 w-4 ${report.color}`} />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-xs text-muted-foreground">
                                        {report.description}
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Report Content */}
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {reportCards.find((r) => r.id === activeTab)?.title}
                        </CardTitle>
                        <CardDescription>
                            {reportCards.find((r) => r.id === activeTab)?.description}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {activeTab === 'hospital' && <HospitalReport />}
                        {activeTab === 'premature' && <PrematureStatisticsReport />}
                        {activeTab === 'von-compliance' && <VonComplianceReport />}
                        {activeTab === 'trends' && <TrendsAnalysisReport />}
                        {activeTab === 'quality' && <QualityIndicatorsReport />}
                        {activeTab === 'comparative' && isAdmin && <ComparativeAnalysisReport />}
                    </CardContent>
                </Card>
            </div>
        </MainLayout>
    );
};
